import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Menu, User, Heart, MessageSquare, LogOut, Settings, Home, Globe, Crown, Zap, Plus, HelpCircle, Info, Lightbulb, Headphones } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { SwipeMatchLogo } from '../SwipeMatchLogo';

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();
  const { language, toggleLanguage } = useLanguage();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isHome = location.pathname === '/';
  const isVerhuurder = user?.userType === 'verhuurder';

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      scrolled || !isHome ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-[#E5E5E5]' : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <SwipeMatchLogo size="default" variant={scrolled || !isHome ? "color" : "white"} />
          </Link>

          {/* Right side navigation */}
          <div className="flex items-center gap-2">
            {/* Desktop Navigation - Hidden on mobile */}
            <div className="hidden md:flex items-center gap-2">
              {/* Language Toggle */}
              <Button
                variant="ghost"
                onClick={toggleLanguage}
                className={`h-10 px-3 rounded-full font-medium ${
                  scrolled || !isHome ? 'text-[#2A2A2A] hover:bg-[#E5E5E5]' : 'text-white hover:bg-white/10'
                }`}
              >
                <Globe className="w-4 h-4 mr-1" />
                {language === 'nl' ? 'EN' : 'NL'}
              </Button>

              {isAuthenticated ? (
                <>
                  <Button
                    variant="ghost"
                    onClick={() => navigate(isVerhuurder ? '/verhuurder/dashboard' : '/dashboard')}
                    className={`h-10 px-4 rounded-full font-medium ${
                      scrolled || !isHome ? 'text-[#2A2A2A] hover:bg-[#E5E5E5]' : 'text-white hover:bg-white/10'
                    }`}
                  >
                    <Home className="w-4 h-4 mr-2" />
                    Dashboard
                  </Button>
                  {/* Swipen button - ONLY for huurders */}
                  {!isVerhuurder && (
                    <Button
                      onClick={() => navigate('/swipematch/swipe')}
                      className="bg-[#2E7D32] hover:bg-[#256B2B] text-white h-10 px-5 rounded-full font-semibold text-sm"
                    >
                      <Heart className="w-4 h-4 mr-2" />
                      {language === 'nl' ? 'Swipen' : 'Swipe'}
                    </Button>
                  )}
                  {/* Woning plaatsen button - ONLY for verhuurders */}
                  {isVerhuurder && (
                    <Button
                      onClick={() => navigate('/verhuurder/onboarding')}
                      className="bg-[#2E7D32] hover:bg-[#256B2B] text-white h-10 px-5 rounded-full font-semibold text-sm"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      {language === 'nl' ? 'Woning plaatsen' : 'Add property'}
                    </Button>
                  )}
                </>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    className={`font-medium h-10 px-4 rounded-full ${
                      scrolled || !isHome ? 'text-[#2A2A2A] hover:bg-[#E5E5E5]' : 'text-white hover:bg-white/10'
                    }`}
                    onClick={() => navigate('/login')}
                  >
                    {language === 'nl' ? 'Inloggen' : 'Login'}
                  </Button>
                  <Button
                    className="bg-[#2E7D32] hover:bg-[#256B2B] text-white font-semibold h-10 px-5 rounded-full"
                    onClick={() => navigate('/registreren')}
                  >
                    <Heart className="w-4 h-4 mr-2" />
                    {language === 'nl' ? 'Start gratis' : 'Start free'}
                  </Button>
                </>
              )}
            </div>

            {/* Hamburger Menu - Always visible */}
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className={`w-10 h-10 rounded-full ${
                  scrolled || !isHome ? 'hover:bg-[#E5E5E5]' : 'hover:bg-white/10'
                }`}>
                  <Menu className={`w-6 h-6 ${scrolled || !isHome ? 'text-[#2A2A2A]' : 'text-white'}`} />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full sm:w-[320px] p-0 bg-[#F7F7F7]">
                <div className="flex flex-col h-full">
                {/* Header */}
                <div className="p-5 border-b border-[#E5E5E5] bg-white">
                  <SwipeMatchLogo size="default" variant="color" />
                </div>
                
                {/* User info if logged in */}
                {isAuthenticated && (
                  <div className="p-4 bg-white border-b border-[#E5E5E5]">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#2E7D32] to-[#1F3A5F] flex items-center justify-center text-white font-semibold text-lg">
                        {user?.name?.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-semibold text-[#2A2A2A]">{user?.name}</p>
                        <p className="text-sm text-[#9E9E9E]">{user?.email}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Menu items */}
                <nav className="flex-1 p-4 space-y-2">
                  {/* Language Toggle - Always visible */}
                  <button
                    onClick={toggleLanguage}
                    className="flex items-center justify-between w-full px-4 py-3 bg-[#1F3A5F]/10 rounded-xl text-[#1F3A5F] hover:bg-[#1F3A5F]/20 transition-colors"
                  >
                    <span className="flex items-center gap-2 font-medium">
                      <Globe className="w-4 h-4" />
                      {language === 'nl' ? 'English' : 'Nederlands'}
                    </span>
                    <span className="text-xs bg-[#1F3A5F] text-white px-2 py-0.5 rounded-full">
                      {language === 'nl' ? '🇬🇧' : '🇳🇱'}
                    </span>
                  </button>

                  {isAuthenticated ? (
                    <>
                      {/* Dashboard - always show for logged in users */}
                      <button
                        onClick={() => { navigate(isVerhuurder ? '/verhuurder/dashboard' : '/dashboard'); setIsOpen(false); }}
                        className="flex items-center w-full px-4 py-3 bg-[#2E7D32] text-white rounded-xl font-semibold"
                      >
                        <Home className="w-5 h-5 mr-3" />
                        Dashboard
                      </button>

                      {/* Swipen - ONLY for huurders */}
                      {!isVerhuurder && (
                        <button
                          onClick={() => { navigate('/swipematch/swipe'); setIsOpen(false); }}
                          className="flex items-center w-full px-4 py-3 bg-white rounded-xl text-[#2A2A2A] hover:bg-[#E5E5E5] transition-colors"
                        >
                          <Heart className="w-5 h-5 mr-3 text-[#2E7D32]" />
                          {language === 'nl' ? 'Start swipen' : 'Start swiping'}
                        </button>
                      )}
                      
                      {/* Profile */}
                      <button
                        onClick={() => { navigate(isVerhuurder ? '/verhuurder/account' : '/profiel'); setIsOpen(false); }}
                        className="flex items-center w-full px-4 py-3 bg-white rounded-xl text-[#2A2A2A] hover:bg-[#E5E5E5] transition-colors"
                      >
                        <User className="w-5 h-5 mr-3 text-[#9E9E9E]" />
                        {language === 'nl' ? 'Mijn profiel' : 'My profile'}
                      </button>

                      {/* Woning plaatsen - ONLY for verhuurders */}
                      {isVerhuurder && (
                        <button
                          onClick={() => { navigate('/verhuurder/onboarding'); setIsOpen(false); }}
                          className="flex items-center w-full px-4 py-3 bg-white rounded-xl text-[#2A2A2A] hover:bg-[#E5E5E5] transition-colors"
                        >
                          <Plus className="w-5 h-5 mr-3 text-[#9E9E9E]" />
                          {language === 'nl' ? 'Woning plaatsen' : 'Add property'}
                        </button>
                      )}

                      {/* Separator */}
                      <div className="border-t border-[#E5E5E5] my-2" />
                      
                      {/* Navigation links */}
                      <button
                        onClick={() => { navigate('/hoe-werkt-het'); setIsOpen(false); }}
                        className="flex items-center w-full px-4 py-3 bg-white rounded-xl text-[#2A2A2A] hover:bg-[#E5E5E5] transition-colors"
                      >
                        <HelpCircle className="w-5 h-5 mr-3 text-[#9E9E9E]" />
                        {language === 'nl' ? 'Hoe werkt het?' : 'How it works'}
                      </button>
                      
                      <button
                        onClick={() => { navigate('/over-ons'); setIsOpen(false); }}
                        className="flex items-center w-full px-4 py-3 bg-white rounded-xl text-[#2A2A2A] hover:bg-[#E5E5E5] transition-colors"
                      >
                        <Info className="w-5 h-5 mr-3 text-[#9E9E9E]" />
                        {language === 'nl' ? 'Over swipejewoning' : 'About us'}
                      </button>
                      
                      <button
                        onClick={() => { navigate(isVerhuurder ? '/verhuurder/tips' : '/faq'); setIsOpen(false); }}
                        className="flex items-center w-full px-4 py-3 bg-white rounded-xl text-[#2A2A2A] hover:bg-[#E5E5E5] transition-colors"
                      >
                        <Lightbulb className="w-5 h-5 mr-3 text-[#9E9E9E]" />
                        {language === 'nl' ? 'Handige tips' : 'Useful tips'}
                      </button>
                      
                      <button
                        onClick={() => { navigate('/contact'); setIsOpen(false); }}
                        className="flex items-center w-full px-4 py-3 bg-white rounded-xl text-[#2A2A2A] hover:bg-[#E5E5E5] transition-colors"
                      >
                        <Headphones className="w-5 h-5 mr-3 text-[#9E9E9E]" />
                        Support
                      </button>
                    </>
                  ) : (
                    <>
                      {/* Start gratis - was: Abonneer nu */}
                      <button
                        onClick={() => { navigate('/registreren'); setIsOpen(false); }}
                        className="flex items-center w-full px-4 py-3 bg-[#2E7D32] text-white rounded-xl font-semibold"
                      >
                        <Heart className="w-5 h-5 mr-3" />
                        {language === 'nl' ? 'Start gratis' : 'Start free'}
                      </button>
                      
                      {/* Woning plaatsen */}
                      <button
                        onClick={() => { navigate('/verhuurder/onboarding'); setIsOpen(false); }}
                        className="flex items-center w-full px-4 py-3 bg-white rounded-xl text-[#2A2A2A] hover:bg-[#E5E5E5] transition-colors"
                      >
                        <Plus className="w-5 h-5 mr-3 text-[#9E9E9E]" />
                        {language === 'nl' ? 'Woning plaatsen' : 'Add property'}
                      </button>

                      {/* Separator */}
                      <div className="border-t border-[#E5E5E5] my-2" />

                      {/* Navigation links */}
                      <button
                        onClick={() => { navigate('/hoe-werkt-het'); setIsOpen(false); }}
                        className="flex items-center w-full px-4 py-3 bg-white rounded-xl text-[#2A2A2A] hover:bg-[#E5E5E5] transition-colors"
                      >
                        <HelpCircle className="w-5 h-5 mr-3 text-[#9E9E9E]" />
                        {language === 'nl' ? 'Hoe werkt het?' : 'How it works'}
                      </button>
                      
                      <button
                        onClick={() => { navigate('/over-ons'); setIsOpen(false); }}
                        className="flex items-center w-full px-4 py-3 bg-white rounded-xl text-[#2A2A2A] hover:bg-[#E5E5E5] transition-colors"
                      >
                        <Info className="w-5 h-5 mr-3 text-[#9E9E9E]" />
                        {language === 'nl' ? 'Over swipejewoning' : 'About us'}
                      </button>
                      
                      <button
                        onClick={() => { navigate('/faq'); setIsOpen(false); }}
                        className="flex items-center w-full px-4 py-3 bg-white rounded-xl text-[#2A2A2A] hover:bg-[#E5E5E5] transition-colors"
                      >
                        <Lightbulb className="w-5 h-5 mr-3 text-[#9E9E9E]" />
                        {language === 'nl' ? 'Handige tips' : 'Useful tips'}
                      </button>
                      
                      <button
                        onClick={() => { navigate('/contact'); setIsOpen(false); }}
                        className="flex items-center w-full px-4 py-3 bg-white rounded-xl text-[#2A2A2A] hover:bg-[#E5E5E5] transition-colors"
                      >
                        <Headphones className="w-5 h-5 mr-3 text-[#9E9E9E]" />
                        Support
                      </button>
                    </>
                  )}
                </nav>
                
                {/* Bottom actions */}
                <div className="p-4 border-t border-[#E5E5E5] bg-white">
                  {isAuthenticated ? (
                    <button
                      onClick={() => { handleLogout(); setIsOpen(false); }}
                      className="flex items-center justify-center w-full px-4 py-3 text-[#C04A4A] rounded-xl hover:bg-[#C04A4A]/10 transition-colors"
                    >
                      <LogOut className="w-5 h-5 mr-2" />
                      {language === 'nl' ? 'Uitloggen' : 'Logout'}
                    </button>
                  ) : (
                    <Button
                      className="w-full h-12 bg-[#2E7D32] hover:bg-[#256B2B] text-white rounded-xl font-semibold"
                      onClick={() => { navigate('/login'); setIsOpen(false); }}
                    >
                      <User className="w-5 h-5 mr-2" />
                      {language === 'nl' ? 'Log in' : 'Log in'}
                    </Button>
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
